import { memo, useCallback } from 'react';

import { Badge, Button, CopyText, Flex, Heading, Label, Link, Text } from '@/components/core';
import { InfoIcon, WarningDuotoneIcon } from '@/components/icons';
import { ZapierLogo } from '@/components/logos';
import { useGetAgentFromPath } from '@/hooks';
import { toAppClientId, useGetAppClient, useListAppClients } from '@/hooks/api';
import { useTriggerStore } from '@/store';

export type ExportZapierConfigurationProps = object;

const ZAPIER_BASE_URI = 'https://api.zapier.com/v1/embed/cradl-ai/create';

// eslint-disable-next-line no-empty-pattern
const IExportZapierConfiguration = ({}: ExportZapierConfigurationProps = {}) => {
  const action = useTriggerStore((state) => state.action);
  const update = useTriggerStore((state) => state.updateAction);
  const { data: agent } = useGetAgentFromPath();

  const { data: appClients } = useListAppClients();
  const appClientWithSecret = appClients?.data?.find((appClient) => appClient.hasSecret);

  const { data: appClient } = useGetAppClient(toAppClientId(appClientWithSecret?.appClientId), {
    refetchInterval: (query) => (!query.state.data?.clientSecret ? 2000 : undefined),
  });

  const isConfigured = !!action.config.url;

  const onDisconnect = useCallback(() => {
    update({ config: { ...action.config, url: null } });
  }, [action.config, update]);

  const newZapUrl = `${ZAPIER_BASE_URI}?steps[0][app]=CradlAiCLIAPI@latest&steps[0][action]=agentRunComplete&steps[0][params][agentId]=${agent?.agentId}&steps[0][params][actionId]=${action?.actionId}`;

  const onSetupClicked = useCallback(() => {
    window.open(newZapUrl, '_blank');
  }, [newZapUrl]);

  return (
    <Flex col className="gap-md p-md grow">
      <Flex col className="gap-sm">
        <Label>Account</Label>

        <Flex className="p-sm gap-sm bg-gray-25 items-center rounded-md border-1 border-gray-100">
          <ZapierLogo size="xl" />

          <Heading className="grow">Zapier</Heading>

          {isConfigured ? (
            <Button variant="secondary" onPress={onDisconnect}>
              <Text>Disconnect</Text>
            </Button>
          ) : (
            <Button variant="secondary" onPress={onSetupClicked}>
              <Text>Connect</Text>
            </Button>
          )}
        </Flex>
      </Flex>

      <Flex className="p-sm items-center">
        <Label className="grow text-sm">Connection status</Label>
        <Badge variant={isConfigured ? 'success' : 'secondary'}>
          {isConfigured ? (
            <span className="border-success-100 bg-success-500 size-[0.8rem] rounded-full border-[0.2rem]"></span>
          ) : (
            <span className="size-[0.8rem] rounded-full border-[0.2rem] border-gray-100 bg-gray-500"></span>
          )}
          <Text className="!text-sm">{isConfigured ? 'Connected' : 'Not connected'}</Text>
        </Badge>
      </Flex>

      {!isConfigured ? (
        <>
          <Flex variant="plain" col className="p-md gap-4 rounded-lg border-1 border-gray-100 bg-white shadow-xs">
            <Flex col className="gap-xs">
              <Label>Client ID</Label>
              <CopyText value={appClient?.clientId ?? 'Loading ...'} />
            </Flex>

            <Flex col className="gap-xs">
              <Label>Client Secret</Label>
              <CopyText isSensitive value={appClient?.clientSecret ?? 'Loading ...'} />
            </Flex>
          </Flex>

          <Flex variant="warning" className="p-md gap-sm rounded-lg">
            <WarningDuotoneIcon />

            <Flex col className="border-none">
              <Heading size="sm">Setup required</Heading>
              <Text>
                Make sure your Zap is connected and <strong>published</strong>.
              </Text>
            </Flex>
          </Flex>
        </>
      ) : null}

      <div className="grow" />

      {isConfigured ? (
        <div></div>
      ) : (
        <>
          <Flex col className="group/tutorial-box mb-md rounded-lg border-1 border-gray-200 bg-gray-50 shadow-md">
            <Flex className="p-sm items-center">
              <Heading size="sm" className="gap-xs flex grow">
                <InfoIcon className="text-gray-400" />
                Setting up Zapier
              </Heading>

              <Link href="https://docs.cradl.ai/integrations/zapier" className="font-regular text-sm">
                Read full guide
              </Link>
            </Flex>

            <Flex col className="p-md gap-sm rounded-lg border-1 border-gray-200 bg-white">
              <Text>
                1. Click <i>Connect </i>to create a Zap with your Cradl AI agent as the trigger.
              </Text>
              <Text>
                2. When connecting to Cradl AI from your Zap, use the <i>Client ID</i> and <i>Client Secret</i> provided above.
              </Text>
              <Text>3. Publish your Zap.</Text>
            </Flex>
          </Flex>
        </>
      )}
    </Flex>
  );
};

export const ExportZapierConfiguration = memo(IExportZapierConfiguration) as typeof IExportZapierConfiguration;
